import { useState, useRef, useEffect, Fragment } from 'react';
import type { JSX, RefObject } from 'react';
import { IconSettingsModal } from './IconSettingsModal';
import { InfoModal } from './InfoModal';
import { INFO_DOCS } from './infoContent';
import type { InfoKey } from './infoContent';
import { ItemRow, SimpleItem, MenuSeparator } from './HamburgerMenuItem';
import { useAppIcon } from '../hooks/useAppIcon';
import { useThemeSheet } from '../hooks/useTheme';

/**
 * 「マニュアル / 利用規約 / プライバシーポリシー」の常設項目定義。
 * `separatorBefore` でセクション分けを表現する（マニュアルは単独、規約類は
 * 同一グループ）。クリックすると対応する InfoModal を開く。
 */
const INFO_ITEMS: readonly {
  readonly key: InfoKey;
  readonly icon: string;
  readonly separatorBefore: boolean;
}[] = [
  { key: 'manual', icon: '📖', separatorBefore: true },
  { key: 'terms', icon: '📄', separatorBefore: true },
  { key: 'privacy', icon: '🔒', separatorBefore: false },
];

// ---------------------------------------------------------------------------
// 公開型
// ---------------------------------------------------------------------------

/**
 * ハンバーガーメニューに追加表示するエントリ。
 * 「アイコン設定」は固定で組み込まれているため、それ以外の項目を呼び出し側
 * （Dashboard / ChatView）が `extraItems` として渡す。
 */
export interface HamburgerMenuItem {
  /** 行頭に表示する絵文字アイコン。 */
  readonly icon: string;
  /** メニュー項目のラベル。 */
  readonly label: string;
  /** クリック時のハンドラ。メニューはクリック後に自動的に閉じる。 */
  readonly onClick: () => void;
  /**
   * `true` のとき「PRO」バッジを表示する。課金対象機能であることを視覚的に
   * 示すために使う。現状は機能自体は使えるが、将来 paywall を有効化する
   * ときの UI 上の目印になる。
   */
  readonly premium?: boolean;
  /**
   * 視覚バリアント:
   *   - 'default'  : 標準
   *   - 'danger'   : 赤系（ログアウト等の警戒色）
   */
  readonly variant?: 'default' | 'danger';
  /**
   * `true` のとき、このアイテムの上に区切り線を表示する。
   * 「設定系」と「ログアウト」を視覚的に分離する用途。
   */
  readonly separatorBefore?: boolean;
  /** アクセシビリティ用のオプショナルタイトル。 */
  readonly title?: string;
}

/**
 * メニュー最上部に表示する情報行（クリック不可）。ユーザーの識別子
 * （メールアドレス等）を表示する用途で、選択メニュー領域とは区切り線で
 * 分離される。
 */
export interface HamburgerMenuHeader {
  /** 行頭の絵文字アイコン (省略可)。 */
  readonly icon?: string;
  /** 表示テキスト。 */
  readonly label: string;
  /** ホバー時のタイトル属性 (省略時は label がそのまま表示)。 */
  readonly title?: string;
}

export interface HamburgerMenuProps {
  /**
   * メニュー上部（テーマ等の常設項目より上）に表示する呼び出し側の項目。
   * 例: テンプレート設定 / NGワード設定。順序通り表示する。
   */
  readonly extraItems?: readonly HamburgerMenuItem[];
  /**
   * メニュー最下部（規約類の下・バージョンの上）に表示する呼び出し側の項目。
   * ログアウトなど「締め」の操作を置く用途。順序通り表示する。
   */
  readonly footerItems?: readonly HamburgerMenuItem[];
  /**
   * メニュー最上部に表示する情報行。ボタンではなく情報表示用なので、
   * クリックしてもメニューは閉じない。後続の選択メニューとは
   * 区切り線で分離される。
   */
  readonly header?: HamburgerMenuHeader;
}

// ---------------------------------------------------------------------------
// 内部フック
// ---------------------------------------------------------------------------

/**
 * 要素外クリックで `onOutside` を呼ぶ副作用。`active` の間だけ `mousedown` を
 * 購読する。コールバックは ref に退避して最新を参照しつつ、購読の張り直しは
 * `active` 変化時のみに抑える。
 */
function useDismissOnOutsideClick<T extends HTMLElement>(
  ref: RefObject<T>,
  active: boolean,
  onOutside: () => void,
): void {
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;
  useEffect(() => {
    if (!active) return;
    function handle(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onOutsideRef.current();
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [ref, active]);
}

// ---------------------------------------------------------------------------
// コンポーネント
// ---------------------------------------------------------------------------

export function HamburgerMenu({
  extraItems = [],
  footerItems = [],
  header,
}: HamburgerMenuProps = {}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 開いている情報モーダルの種別。null のときは非表示。
  const [infoKey, setInfoKey] = useState<InfoKey | null>(null);
  const { icon, setIcon } = useAppIcon();
  const themeSheet = useThemeSheet();
  const menuRef = useRef<HTMLDivElement>(null);

  // メニュー外クリックで閉じる。
  useDismissOnOutsideClick(menuRef, isOpen, () => setIsOpen(false));

  return (
    <div className="hamburger-menu" ref={menuRef}>
      <button
        type="button"
        className="hamburger-menu__button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="メニューを開く"
        aria-expanded={isOpen}
      >
        <svg
          className="hamburger-menu__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      </button>

      {isOpen && (
        <div className="hamburger-menu__dropdown" role="menu">
          {/* メニュー最上部の情報行 (ユーザー識別等)。クリック不可で、
              下の選択メニューとは区切り線で分離する。 */}
          {header && (
            <>
              <div
                className="hamburger-menu__header"
                role="presentation"
                title={header.title ?? header.label}
              >
                {header.icon && (
                  <span aria-hidden="true" className="hamburger-menu__item-icon">
                    {header.icon}
                  </span>
                )}
                <span className="hamburger-menu__header-label">{header.label}</span>
              </div>
              <MenuSeparator />
            </>
          )}

          {/* 上部: 呼び出し側の項目（テンプレート設定 / NGワード設定 等） */}
          {extraItems.map((item, idx) => (
            <ItemRow
              key={`extra-${item.label}-${idx}`}
              item={item}
              onClickWrapped={() => {
                setIsOpen(false);
                item.onClick();
              }}
            />
          ))}

          {/* 設定グループ: テーマ / アイコン設定 (テーマは Context 提供時のみ) */}
          {extraItems.length > 0 && <MenuSeparator />}
          {themeSheet !== null && (
            <SimpleItem
              icon="🎨"
              label="テーマ"
              onClick={() => {
                setIsOpen(false);
                themeSheet.open();
              }}
            />
          )}
          <SimpleItem
            icon="⚙️"
            label="アイコン設定"
            onClick={() => {
              setIsOpen(false);
              setIsModalOpen(true);
            }}
          />

          {/* マニュアル → 規約類。マニュアルは単独セクション、利用規約と
              プライバシーポリシーは同一セクションにまとめる。 */}
          {INFO_ITEMS.map(({ key, icon: infoIcon, separatorBefore }) => (
            <Fragment key={key}>
              {separatorBefore && <MenuSeparator />}
              <SimpleItem
                icon={infoIcon}
                label={INFO_DOCS[key].title}
                onClick={() => {
                  setIsOpen(false);
                  setInfoKey(key);
                }}
              />
            </Fragment>
          ))}

          {/* 下部: 呼び出し側の項目（ログアウト 等） */}
          {footerItems.length > 0 && <MenuSeparator />}
          {footerItems.map((item, idx) => (
            <ItemRow
              key={`footer-${item.label}-${idx}`}
              item={item}
              onClickWrapped={() => {
                setIsOpen(false);
                item.onClick();
              }}
            />
          ))}

          {/* 最下段: アプリのバージョン（クリック不可の情報表示）。 */}
          <MenuSeparator />
          <div className="hamburger-menu__version" role="presentation">
            v{__APP_VERSION__}
            <span className="hamburger-menu__beta-badge">Beta</span>
          </div>
        </div>
      )}

      {isModalOpen && (
        <IconSettingsModal
          currentIcon={icon}
          onSelect={(selected) => setIcon(selected)}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {infoKey !== null && (
        <InfoModal doc={INFO_DOCS[infoKey]} onClose={() => setInfoKey(null)} />
      )}
    </div>
  );
}
